import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api, RequestOptions, ApiError } from '../utils/api';
import { ImpersonationManager } from '../utils/impersonationManager';
import { useDemoMode } from './useDemoMode';
import toast from 'react-hot-toast';

/**
 * Hook that provides an API client with automatic authentication for both regular and demo modes
 * - In regular mode: Includes JWT token and impersonation headers when active
 * - In demo mode: Includes demo access key as Bearer DEMO-{key} token
 */
// Track last workspace access error to prevent duplicate notifications
let lastWorkspaceAccessError = 0;
const WORKSPACE_ACCESS_ERROR_COOLDOWN = 2000; // 2 seconds

export const useAuthenticatedApi = () => {
  const { getAccessToken, isAuthenticated } = useAuth();
  const { isDemoMode, demoAccessKey } = useDemoMode();
  const navigate = useNavigate();

  /**
   * Handle API errors and redirect on workspace access denied
   * Implements cooldown to prevent duplicate notifications from simultaneous failed requests
   * Redirects to /select-project where user can see clickable message to fix the issue
   */
  const handleApiError = (error: any) => {
    console.error('useAuthenticatedApi handleApiError', error);
    if (error instanceof ApiError && error.status === 403) {
      const message = error.message || '';
      // Check if this is a workspace access denial (template requirements)
      if (message.includes('Access denied:')) {
        const now = Date.now();
        
        // Only show notification and redirect if cooldown period has passed
        if (now - lastWorkspaceAccessError > WORKSPACE_ACCESS_ERROR_COOLDOWN) {
          lastWorkspaceAccessError = now;
          toast.error(message);
          // Redirect to project selection where user can see the project card
          // with a clickable message to open account settings and fix the issue
          navigate('/select-project', { replace: true });
        }
        
        return; // Don't re-throw after handling
      }
    }
    // Re-throw all other errors
    throw error;
  };


  return useMemo(() => {
    const getCurrentToken = async (): Promise<string | null> => {
      return isDemoMode && demoAccessKey ? `DEMO-${demoAccessKey}` : await getAccessToken();
    };

    const buildAuthenticatedRequestOptions = async (options: RequestInit = {}): Promise<RequestInit> => {
      const currentToken = await getCurrentToken();
      const headers = new Headers(options.headers);

      if (currentToken) {
        headers.set('Authorization', `Bearer ${currentToken}`);
      }

      if (!isDemoMode) {
        const impersonationUserId = ImpersonationManager.getCurrentImpersonationUserId();
        if (impersonationUserId) {
          headers.set('X-Impersonate-User-ID', impersonationUserId);
        }
      }

      return {
        ...options,
        headers,
      };
    };

    const buildApiError = async (response: Response, fallbackMessage: string): Promise<ApiError> => {
      const data = await response.json().catch(() => ({}));
      const message = data.detail || data.message || response.statusText || fallbackMessage;
      return new ApiError(message, response.status, data);
    };

    return ({
    /**
     * Make a GET request with automatic token injection and impersonation
     */
    get: async <T = any>(endpoint: string, options: RequestInit = {}): Promise<T> => {
      try {
        const currentToken = isDemoMode && demoAccessKey ? `DEMO-${demoAccessKey}` : await getAccessToken();
        const requestOptions: RequestOptions = { ...options, token: currentToken };

        // Add impersonation header if currently impersonating (not for demo mode)
        if (!isDemoMode) {
          const impersonationUserId = ImpersonationManager.getCurrentImpersonationUserId();
          if (impersonationUserId) {
            requestOptions.impersonateUserId = impersonationUserId;
          }
        }

        return await api.get<T>(endpoint, requestOptions);
      } catch (error) {
        handleApiError(error);
        throw error; // This won't be reached if handleApiError navigates
      }
    },

    /**
     * Make a POST request with automatic token injection and impersonation
     */
    post: async <T = any>(endpoint: string, data?: any, options: RequestInit = {}): Promise<T> => {
      try {
        const currentToken = isDemoMode && demoAccessKey ? `DEMO-${demoAccessKey}` : await getAccessToken();
        const requestOptions: RequestOptions = { ...options, token: currentToken };

        // Add impersonation header if currently impersonating (not for demo mode)
        if (!isDemoMode) {
          const impersonationUserId = ImpersonationManager.getCurrentImpersonationUserId();
          if (impersonationUserId) {
            requestOptions.impersonateUserId = impersonationUserId;
          }
        }

        return await api.post<T>(endpoint, data, requestOptions);
      } catch (error) {
        handleApiError(error);
        throw error;
      }
    },

    /**
     * Make a PUT request with automatic token injection and impersonation
     */
    put: async <T = any>(endpoint: string, data?: any, options: RequestInit = {}): Promise<T> => {
      try {
        const currentToken = isDemoMode && demoAccessKey ? `DEMO-${demoAccessKey}` : await getAccessToken();
        const requestOptions: RequestOptions = { ...options, token: currentToken };

        // Add impersonation header if currently impersonating (not for demo mode)
        if (!isDemoMode) {
          const impersonationUserId = ImpersonationManager.getCurrentImpersonationUserId();
          if (impersonationUserId) {
            requestOptions.impersonateUserId = impersonationUserId;
          }
        }

        return await api.put<T>(endpoint, data, requestOptions);
      } catch (error) {
        handleApiError(error);
        throw error;
      }
    },

    /**
     * Make a DELETE request with automatic token injection and impersonation
     */
    delete: async <T = any>(endpoint: string, options: RequestInit = {}): Promise<T> => {
      try {
        const currentToken = isDemoMode && demoAccessKey ? `DEMO-${demoAccessKey}` : await getAccessToken();
        const requestOptions: RequestOptions = { ...options, token: currentToken };

        // Add impersonation header if currently impersonating (not for demo mode)
        if (!isDemoMode) {
          const impersonationUserId = ImpersonationManager.getCurrentImpersonationUserId();
          if (impersonationUserId) {
            requestOptions.impersonateUserId = impersonationUserId;
          }
        }

        return await api.delete<T>(endpoint, requestOptions);
      } catch (error) {
        handleApiError(error);
        throw error;
      }
    },

    /**
     * Make a PATCH request with automatic token injection and impersonation
     */
    patch: async <T = any>(endpoint: string, data?: any, options: RequestInit = {}): Promise<T> => {
      try {
        const currentToken = isDemoMode && demoAccessKey ? `DEMO-${demoAccessKey}` : await getAccessToken();
        const requestOptions: RequestOptions = { ...options, token: currentToken };

        // Add impersonation header if currently impersonating (not for demo mode)
        if (!isDemoMode) {
          const impersonationUserId = ImpersonationManager.getCurrentImpersonationUserId();
          if (impersonationUserId) {
            requestOptions.impersonateUserId = impersonationUserId;
          }
        }

        return await api.patch<T>(endpoint, data, requestOptions);
      } catch (error) {
        handleApiError(error);
        throw error;
      }
    },

    uploadFile: async (endpoint: string, formData: FormData, options: RequestInit = {}) => {
        const requestOptions = await buildAuthenticatedRequestOptions({
          ...options,
          method: 'POST',
          body: formData,
        });

        const response = await fetch(`${api.getBaseUrl()}${endpoint}`, requestOptions);
        const json = await response.json();

        if (response.ok) {
          return json;
        }

        throw new Error(json.detail || 'Unknown error');
      },

      /**
       * Fetch a binary response with the same auth and impersonation behavior as JSON API calls.
       */
      downloadBlob: async (
        endpoint: string,
        options: RequestInit = {}
      ): Promise<{ blob: Blob; headers: Headers; response: Response }> => {
        try {
          const requestOptions = await buildAuthenticatedRequestOptions({
            ...options,
            method: options.method || 'GET',
          });

          const response = await fetch(`${api.getBaseUrl()}${endpoint}`, requestOptions);

          if (!response.ok) {
            throw await buildApiError(response, 'Download failed');
          }

          return {
            blob: await response.blob(),
            headers: response.headers,
            response,
          };
        } catch (error) {
          handleApiError(error);
          throw error;
        }
      },

      /**
       * Get the base URL for the API
       */
      getBaseUrl: () => api.getBaseUrl(),

      /**
       * Get the current auth token (JWT or demo token)
       */
      getToken: async (): Promise<string | null> => {
        return getCurrentToken();
      },

      /**
       * Make an admin request that never impersonates (for admin-only operations)
       */
      adminRequest: async <T = any>(
        method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
        endpoint: string,
        data?: any,
        options: RequestInit = {}
      ): Promise<T> => {
        // Admin requests don't work in demo mode
        if (isDemoMode) {
          throw new Error('Admin requests are not available in demo mode');
        }

        const currentToken = await getAccessToken();
        const requestOptions: RequestOptions = { ...options, token: currentToken };
        // Explicitly don't add impersonation headers for admin requests

        switch (method) {
          case 'GET':
            return api.get<T>(endpoint, requestOptions);
          case 'POST':
            return api.post<T>(endpoint, data, requestOptions);
          case 'PUT':
            return api.put<T>(endpoint, data, requestOptions);
          case 'DELETE':
            return api.delete<T>(endpoint, requestOptions);
          case 'PATCH':
            return api.patch<T>(endpoint, data, requestOptions);
          default:
            throw new Error(`Unsupported method: ${method}`);
        }
      },

      /**
       * Check if user is authenticated
       */
      isAuthenticated,

      /**
       * Get current impersonation state
       */
      getImpersonationState: () => ImpersonationManager.getCurrentState(),
    });
  }, [getAccessToken, isAuthenticated, isDemoMode, demoAccessKey, navigate]);
};

export default useAuthenticatedApi;
